/**
 * Typed fetch client for /api/v2 – the web client talks to the backend exclusively through it.
 *
 *  - credentials: same-origin (session cookie `creator_session`, HttpOnly)
 *  - unsafe methods carry `X-CSRF-Token` (token from /auth/login or /auth/me, see stores/session)
 *  - 401 → `onUnauthorized` hook (router: redirect to /login?redirect=…)
 *  - error statuses map to typed errors (422 field errors, 423 lock, 412 stale ETag, 429 Retry-After)
 *  - ETag: `getWithEtag` returns the ETag; `ifNoneMatch` → `notModified`; `ifMatch` for writes
 */

export interface FieldError {
  column: string;
  code: string;
  message?: string;
  params?: Record<string, unknown>;
}

export interface ErrorBody {
  error?: string;
  message?: string;
  details?: { fields?: FieldError[]; [k: string]: unknown };
  lock?: { user?: string; since?: string; expiresAt?: string };
}

export class ApiError extends Error {
  override name = 'ApiError';
  constructor(
    readonly status: number,
    /** machine readable code (`error` of the body, else `http.<status>`) */
    readonly code: string,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}
export class UnauthorizedError extends ApiError {
  override name = 'UnauthorizedError';
}
export class ForbiddenError extends ApiError {
  override name = 'ForbiddenError';
}
export class NotFoundError extends ApiError {
  override name = 'NotFoundError';
}
export class ConflictError extends ApiError {
  override name = 'ConflictError';
}
/** 412: If-Match did not match – the record was changed by someone else. */
export class PreconditionFailedError extends ApiError {
  override name = 'PreconditionFailedError';
}
/** 422: validation failed; `fields` carries the per column problems. */
export class ValidationError extends ApiError {
  override name = 'ValidationError';
  get fields(): FieldError[] {
    return (this.body as ErrorBody | undefined)?.details?.fields ?? [];
  }
}
/** 423: record locked by another user. */
export class LockedError extends ApiError {
  override name = 'LockedError';
  get lockedBy(): string | undefined {
    const b = this.body as ErrorBody | undefined;
    return b?.lock?.user ?? /locked by (\S+)/.exec(this.message)?.[1];
  }
}
/** 429: rate limited / login throttle; `retryAfter` in seconds. */
export class RateLimitedError extends ApiError {
  override name = 'RateLimitedError';
  constructor(status: number, code: string, message: string, body: unknown, readonly retryAfter: number | undefined) {
    super(status, code, message, body);
  }
}
/** No HTTP response at all (offline, DNS, CORS, aborted proxy …). */
export class NetworkError extends Error {
  override name = 'NetworkError';
  constructor(readonly cause?: unknown) {
    super('network error');
  }
}

export type Query = Record<string, string | number | boolean | null | undefined | readonly (string | number)[]>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  headers?: Record<string, string>;
  ifMatch?: string;
  ifNoneMatch?: string;
  signal?: AbortSignal;
  /** do not call onUnauthorized for a 401 (login, /auth/me probing) */
  allowUnauthorized?: boolean;
}

export interface ApiResponse<T> {
  status: number;
  data: T;
  etag: string | undefined;
  /** 304 on If-None-Match: `data` is undefined */
  notModified: boolean;
  headers: Headers;
}

export interface ApiClientOptions {
  base?: string;
  fetch?: typeof fetch;
  csrfToken?: () => string | undefined;
  onUnauthorized?: (err: UnauthorizedError) => void;
  /** request start/end (loading bar) */
  onActivity?: (delta: 1 | -1) => void;
}

const SAFE = new Set(['GET', 'HEAD', 'OPTIONS']);

export function buildQuery(query: Query | undefined): string {
  if (!query) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) v.forEach((x) => qs.append(k, String(x)));
    else qs.append(k, String(v as string | number | boolean));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

function parseRetryAfter(v: string | null): number | undefined {
  if (!v) return undefined;
  const n = Number(v);
  if (Number.isFinite(n)) return Math.max(0, Math.ceil(n));
  const d = Date.parse(v);
  return Number.isNaN(d) ? undefined : Math.max(0, Math.ceil((d - Date.now()) / 1000));
}

/** Map an error response to a typed error. */
export function toApiError(status: number, body: unknown, headers?: Headers, statusText = ''): ApiError {
  const b = (body && typeof body === 'object' ? body : {}) as ErrorBody;
  const code = typeof b.error === 'string' ? b.error : `http.${status}`;
  const message = typeof b.message === 'string' ? b.message : typeof body === 'string' && body ? body.slice(0, 200) : statusText || `HTTP ${status}`;
  switch (status) {
    case 401:
      return new UnauthorizedError(status, code, message, body);
    case 403:
      return new ForbiddenError(status, code, message, body);
    case 404:
      return new NotFoundError(status, code, message, body);
    case 409:
      return new ConflictError(status, code, message, body);
    case 412:
    case 428:
      return new PreconditionFailedError(status, code, message, body);
    case 422:
      return new ValidationError(status, code, message, body);
    case 423:
      return new LockedError(status, code, message, body);
    case 429:
      return new RateLimitedError(status, code, message, body, parseRetryAfter(headers?.get('retry-after') ?? null));
    default:
      return new ApiError(status, code, message, body);
  }
}

export class ApiClient {
  constructor(private opts: ApiClientOptions = {}) {}

  configure(opts: Partial<ApiClientOptions>): void {
    this.opts = { ...this.opts, ...opts };
  }

  async request<T>(method: string, path: string, o: RequestOptions = {}): Promise<ApiResponse<T>> {
    const m = method.toUpperCase();
    const headers: Record<string, string> = { Accept: 'application/json', ...o.headers };
    let body: BodyInit | undefined;
    if (o.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(o.body);
    }
    if (!SAFE.has(m)) {
      const token = this.opts.csrfToken?.();
      if (token) headers['X-CSRF-Token'] = token;
    }
    if (o.ifMatch) headers['If-Match'] = o.ifMatch;
    if (o.ifNoneMatch) headers['If-None-Match'] = o.ifNoneMatch;

    const url = `${this.opts.base ?? '/api/v2'}${path}${buildQuery(o.query)}`;
    const doFetch = this.opts.fetch ?? globalThis.fetch.bind(globalThis);
    this.opts.onActivity?.(1);
    let res: Response;
    try {
      res = await doFetch(url, { method: m, credentials: 'same-origin', headers, ...(body !== undefined ? { body } : {}), ...(o.signal ? { signal: o.signal } : {}) });
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') throw e;
      throw new NetworkError(e);
    } finally {
      this.opts.onActivity?.(-1);
    }
    const etag = res.headers.get('etag') ?? undefined;
    if (res.status === 304) return { status: 304, data: undefined as T, etag: etag ?? o.ifNoneMatch, notModified: true, headers: res.headers };
    let data: unknown = undefined;
    if (res.status !== 204) {
      const text = await res.text();
      if (text) {
        if (res.headers.get('content-type')?.includes('json')) {
          try {
            data = JSON.parse(text);
          } catch {
            data = text;
          }
        } else data = text;
      }
    }
    if (!res.ok) {
      const err = toApiError(res.status, data, res.headers, res.statusText);
      if (err instanceof UnauthorizedError && !o.allowUnauthorized) this.opts.onUnauthorized?.(err);
      throw err;
    }
    return { status: res.status, data: data as T, etag, notModified: false, headers: res.headers };
  }

  /**
   * Raw request (file uploads with FormData, file downloads): CSRF token and session cookie like
   * request(); the body is sent as is; non-2xx answers throw the same ApiErrors.
   */
  async raw(method: string, path: string, o: { body?: BodyInit; json?: unknown; query?: Query; headers?: Record<string, string>; signal?: AbortSignal } = {}): Promise<Response> {
    const m = method.toUpperCase();
    const headers: Record<string, string> = { ...o.headers };
    let body = o.body;
    if (o.json !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(o.json);
    }
    if (!SAFE.has(m)) {
      const token = this.opts.csrfToken?.();
      if (token) headers['X-CSRF-Token'] = token;
    }
    const doFetch = this.opts.fetch ?? globalThis.fetch.bind(globalThis);
    this.opts.onActivity?.(1);
    let res: Response;
    try {
      res = await doFetch(`${this.opts.base ?? '/api/v2'}${path}${buildQuery(o.query)}`, { method: m, credentials: 'same-origin', headers, ...(body !== undefined ? { body } : {}), ...(o.signal ? { signal: o.signal } : {}) });
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') throw e;
      throw new NetworkError(e);
    } finally {
      this.opts.onActivity?.(-1);
    }
    if (!res.ok) {
      const text = await res.text();
      let data: unknown = text;
      try {
        data = JSON.parse(text);
      } catch {
        // not JSON
      }
      const err = toApiError(res.status, data, res.headers, res.statusText);
      if (err instanceof UnauthorizedError) this.opts.onUnauthorized?.(err);
      throw err;
    }
    return res;
  }

  async get<T>(path: string, o?: RequestOptions): Promise<T> {
    return (await this.request<T>('GET', path, o)).data;
  }
  getWithEtag<T>(path: string, o?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path, o);
  }
  async post<T>(path: string, body?: unknown, o?: RequestOptions): Promise<T> {
    return (await this.request<T>('POST', path, { ...o, body })).data;
  }
  async put<T>(path: string, body?: unknown, o?: RequestOptions): Promise<T> {
    return (await this.request<T>('PUT', path, { ...o, body })).data;
  }
  async patch<T>(path: string, body?: unknown, o?: RequestOptions): Promise<T> {
    return (await this.request<T>('PATCH', path, { ...o, body })).data;
  }
  async delete<T = void>(path: string, o?: RequestOptions): Promise<T> {
    return (await this.request<T>('DELETE', path, o)).data;
  }
}

/** The application-wide client (configured in main.ts: CSRF token, 401 handling, loading bar). */
export const api = new ApiClient();

/** Path segment helper. */
export const seg = (s: string | number): string => encodeURIComponent(String(s));
